import {
  useSafeUserReviews_userProfile$data,
  useSafeUserReviews_userProfile$key,
} from '#app/__generated__/relay/useSafeUserReviews_userProfile.graphql.js';
import { graphql, useFragment } from '@contra/relay';

type UseSafeUserReviewsPayload = {
  averageRating: number;
  reviews: Array<
    useSafeUserReviews_userProfile$data['reviews']['edges'][0]['node']
  >;
};

/**
 * Hook that returns a safe version of user reviews that are positive,
 * comments are visible, and not hidden.
 */
export const useSafeUserReviews = ({
  userProfileRef,
}: {
  userProfileRef?: useSafeUserReviews_userProfile$key;
}): UseSafeUserReviewsPayload => {
  const userProfile = useFragment<useSafeUserReviews_userProfile$key>(
    graphql`
      fragment useSafeUserReviews_userProfile on UserProfile {
        reviews: userReviews(
          filter: { isPositiveReview: true, onlyVisibleToRecipient: false }
        ) {
          edges {
            node {
              id
              commentOnlyVisibleToRecipient
              onlyVisibleToRecipient
              isPositiveReview
              rating
              author {
                avatarImage {
                  id
                  uid
                  url
                }
              }
            }
          }
        }
      }
    `,
    userProfileRef,
  );

  if (!userProfile) {
    return {
      averageRating: 0,
      reviews: [],
    };
  }

  const usableReviewNodes = userProfile.reviews.edges
    .filter(
      ({
        node: {
          commentOnlyVisibleToRecipient,
          isPositiveReview,
          onlyVisibleToRecipient,
        },
      }) =>
        isPositiveReview &&
        !onlyVisibleToRecipient &&
        !commentOnlyVisibleToRecipient,
    )
    .map(({ node }) => ({
      author: {
        avatarImage: node.author.avatarImage
          ? {
              id: node.author.avatarImage.id,
              uid: node.author.avatarImage.uid,
              url: node.author.avatarImage.url,
            }
          : null,
      },
      commentOnlyVisibleToRecipient: node.commentOnlyVisibleToRecipient,
      id: node.id,
      isPositiveReview: node.isPositiveReview,
      onlyVisibleToRecipient: node.onlyVisibleToRecipient,
      rating: node.rating,
    }));

  const averageRating =
    usableReviewNodes.reduce((total, { rating }) => total + rating, 0) /
    usableReviewNodes.length;

  return {
    averageRating,
    reviews: usableReviewNodes,
  };
};
